// src/components/results/ReviewMode.jsx
//
// "Review my mistakes": every wrong answer as a corrected card, confident mistakes
// first (the ones you learn most from), filterable by section. Opens on one part when
// it is started from that part's tab (`initialFilter`). Rendered into <body>
// so no transformed parent can trap the fixed overlay.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import MistakeCard from './MistakeCard';
import { marginNote, itemKey } from './storyText';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const SECTION_NAME = { lesen: 'Lesen', sprachbausteine: 'Sprachbausteine', hoeren: 'Hören' };

export default function ReviewMode({ items, qIndex, whereOf, idx, exam, formatAnswer, isFree, onPractice, onLocked, hasFlags, initialFilter = 'all', norms, onClose }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState(initialFilter);
  const closeRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);

  const chips = useMemo(() => {
    const list = [['all', t('examResults.story.filterAll'), () => true]];
    if (hasFlags) list.push(['sure', t('examResults.story.youWereSure'), (it) => it.confident]);
    Object.keys(SECTION_NAME).forEach((s) => list.push([s, SECTION_NAME[s], (it) => it.section === s]));
    return list.map(([id, label, test]) => ({ id, label, test, count: items.filter(test).length })).filter((c) => c.id === 'all' || c.count > 0);
  }, [items, hasFlags, t]);
  const active = chips.find((c) => c.id === filter) || chips[0];
  const shown = items.filter(active.test);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-title"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      className="fixed inset-0 z-[60] overflow-y-auto bg-[#FAF8F3] text-stone-900"
    >
      <div className="max-w-[980px] mx-auto px-4 sm:px-6 pt-5 sm:pt-8 pb-28 flex flex-col gap-6">
        <header className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h2 id="review-title" className="m-0 text-[28px] sm:text-[40px] leading-[1.05] font-bold tracking-tight" style={DISPLAY}>{t('examResults.story.reviewTitle')}</h2>
            <p className="m-0 text-stone-600 max-w-[60ch]">{hasFlags ? t('examResults.story.reviewIntro') : t('examResults.story.reviewIntroPaper')}</p>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label={t('examResults.story.close')} className="rs-focus shrink-0 w-12 h-12 rounded-full bg-white border border-stone-300 inline-flex items-center justify-center touch-manipulation">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </header>

        <nav aria-label={t('examResults.story.filterLabel')} className="flex flex-wrap gap-2">
          {chips.map((c) => {
            const on = c.id === active.id;
            return (
              <button key={c.id} type="button" aria-pressed={on} onClick={() => setFilter(c.id)}
                className={`rs-focus min-h-[44px] px-4 rounded-full text-sm font-semibold border-2 touch-manipulation ${on ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-900 border-stone-300'}`}>
                {c.label} · {c.count}
              </button>
            );
          })}
        </nav>

        <div className="flex flex-col gap-5">
          {shown.map((it) => {
            const { q, part } = qIndex[itemKey(it)];
            return (
              <MistakeCard
                key={itemKey(it)}
                item={it} q={q} part={part} where={whereOf(it)} idx={idx} exam={exam}
                formatAnswer={formatAnswer} isFree={isFree} onPractice={onPractice} onLocked={onLocked}
                note={marginNote(it, q, part, formatAnswer)}
                norms={norms}
              />
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
